import React, { useEffect, useRef, useState } from 'react';

import { useWaApi } from '../../auth/helper/api/waApi';
import { useUserProfileAccess } from '../../auth/helper/useUserProfileAccess';

const FB_SDK_SRC = 'https://connect.facebook.net/en_US/sdk.js';

// Embedded Signup v4. Meta picks the version from the Facebook Login for Business configuration (config_id) and its products, not
// from the code: v2 / v3 asked for theirs through extras (sessionInfoVersion, version, featureType, features) and are retired, so
// none of those is sent any more. FB_GRAPH_VERSION is the Graph API version of the SDK itself, which this flow barely uses.
const FB_GRAPH_VERSION = 'v26.0';
const EMBEDDED_SIGNUP_V4_EXTRAS = { setup: {} };

// Meta-hosted Embedded Signup: instead of FB.login in this page, the client finishes the signup on a page Meta hosts. Nothing comes back
// to the browser (no code to submit) - Meta reports the finished signup to the service by webhook.
const HOSTED_SIGNUP_URL = 'https://business.facebook.com/messaging/whatsapp/onboard/';
const HOSTED_SIGNUP_EXTRAS = { sessionInfoVersion: '3', version: 'v4' };

// The window features that make the browser open a popup window (centred on this one) like the one FB.login opens, not a tab
const hostedSignupWindowFeatures = () => {
  const width = 720;
  const height = 800;
  const left = Math.max(0, Math.round((window.screenX || 0) + ((window.outerWidth || width) - width) / 2));
  const top = Math.max(0, Math.round((window.screenY || 0) + ((window.outerHeight || height) - height) / 2));
  return `popup=yes,width=${width},height=${height},left=${left},top=${top},scrollbars=yes,resizable=yes,noopener,noreferrer`;
};

const hostedSignupUrl = () => `${HOSTED_SIGNUP_URL}?app_id=${encodeURIComponent(process.env.REACT_APP_WA_META_EMBEDDED_SIGNUP_APP_ID)}`
  + `&config_id=${encodeURIComponent(process.env.REACT_APP_WA_META_EMBEDDED_SIGNUP_CONFIG_ID)}`
  + `&extras=${encodeURIComponent(JSON.stringify(HOSTED_SIGNUP_EXTRAS))}`;

// What Meta's WA_EMBEDDED_SIGNUP message says when the signup is abandoned or fails (CANCEL / ERROR), for the page to show
const describeSignupIssue = (data) => {
  if (data?.error_message) {
    return `Meta reported an error${data.error_code ? ` (${data.error_code})` : ''}: ${data.error_message}`;
  }
  if (data?.current_step) {
    return `The signup was abandoned at the step "${data.current_step}".`;
  }
  return null;
};

// Both ways of signing up (Meta's window opened from this page, and Meta's hosted page) need the app id and the configuration id. (Read when called, not at load.)
const metaIdProblems = () => {
  const missing = [];
  if (!process.env.REACT_APP_WA_META_EMBEDDED_SIGNUP_APP_ID) missing.push('REACT_APP_WA_META_EMBEDDED_SIGNUP_APP_ID (the App id)');
  if (!process.env.REACT_APP_WA_META_EMBEDDED_SIGNUP_CONFIG_ID) missing.push('REACT_APP_WA_META_EMBEDDED_SIGNUP_CONFIG_ID (the Embedded Signup configuration id)');
  if (missing.length === 0) {
    return [];
  }
  return [`Embedded Signup is not configured: ${missing.join(' and ')} ${missing.length > 1 ? 'are' : 'is'} empty. Set `
    + `${missing.length > 1 ? 'them' : 'it'} in the UI's .env and rebuild the UI.`];
};

// What keeps Meta's signup window from ever opening. Each of these makes FB.login do nothing at all - Facebook's SDK only writes
// a line to the browser console - so they are checked up front and shown on the page instead. (Read when called, not at load.)
const embeddedSignupSetupProblems = () => {
  const problems = metaIdProblems();
  // Facebook refuses FB.login on any http page, localhost included
  if (window.location.protocol !== 'https:') {
    problems.push(`Facebook only opens Embedded Signup from an https page, and this page is served over ${window.location.protocol.replace(':', '')}. `
      + 'Open the application through its https address.');
  }
  return problems;
};

// The "OR" between the two ways to sign up: a purple badge between two fading lines, in the buttons' own purple
const OR_DIVIDER_STYLE = { display: 'inline-flex', alignItems: 'center', gap: '8px' };
const OR_LINE_STYLE = { display: 'inline-block', width: '28px', height: '2px', borderRadius: '2px' };
const OR_BADGE_STYLE = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '40px',
  height: '40px',
  borderRadius: '50%',
  background: 'linear-gradient(135deg, #f6ecfa, #e3cdee)',
  border: '2px solid #c9a4dc',
  boxShadow: '0 2px 8px rgba(105, 46, 130, 0.18)',
  color: '#692e82',
  fontSize: '12px',
  fontWeight: 700,
  letterSpacing: '1px',
};

const EmbeddedSignupFlow = () => {

  const waApi = useWaApi();
  const [sdkReady, setSdkReady] = useState(false);
  const [signupResult, setSignupResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);
  const [sdkError, setSdkError] = useState(null);
  const sessionInfoRef = useRef({ wabaId: null, phoneNumberId: null, businessId: null, issue: null });

  useEffect(() => {

    window.fbAsyncInit = function () {
      try {
        window.FB.init({
          appId: process.env.REACT_APP_WA_META_EMBEDDED_SIGNUP_APP_ID,
          cookie: true,
          xfbml: false,
          version: FB_GRAPH_VERSION,
        });
        setSdkReady(true);
      } catch (e) {
        setSdkError(`Facebook's SDK could not be started: ${e.message}`);
      }
    };

    if (!document.getElementById('facebook-jssdk')) {
      const script = document.createElement('script');
      script.id = 'facebook-jssdk';
      script.src = FB_SDK_SRC;
      script.async = true;
      script.defer = true;
      // a blocked or unreachable connect.facebook.net (ad blocker, firewall, no internet) would otherwise leave the button on "Loading"
      script.onerror = () => setSdkError("Facebook's SDK could not be loaded from connect.facebook.net - check the network, a firewall or an ad blocker.");
      document.body.appendChild(script);
    } else if (window.FB) {
      setSdkReady(true);
    }

    // Meta posts embedded-signup session events (WA_EMBEDDED_SIGNUP) to the window
    // via postMessage rather than the FB.login callback, so we must listen separately.
    const handleMessage = (event) => {
      if (!event.origin.endsWith('facebook.com')) {
        return;
      }
      try {
        const data = JSON.parse(event.data);
        if (data.type === 'WA_EMBEDDED_SIGNUP') {
          if (typeof data.event === 'string' && data.event.startsWith('FINISH')) {
            // FINISH, and v4's FINISH_ONLY_WABA, FINISH_WHATSAPP_BUSINESS_APP_ONBOARDING, ...: the ids of the new account
            sessionInfoRef.current = {
              wabaId: data.data?.waba_id ?? null,
              phoneNumberId: data.data?.phone_number_id ?? null,
              businessId: data.data?.business_id ?? null,
              issue: null,
            };
          } else if (data.event === 'CANCEL' || data.event === 'ERROR') {
            sessionInfoRef.current = { ...sessionInfoRef.current, issue: describeSignupIssue(data.data) };
          }
        }
      } catch (e) {
        // Non-JSON postMessage payloads from other Facebook widgets are expected; ignore them.
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  // Re-read on every render so a fixed setup shows as soon as it is fixed
  const setupProblems = embeddedSignupSetupProblems();
  // Meta's hosted page is just a link, so unlike FB.login it works from an http page: only the ids have to be there
  const hostedProblems = metaIdProblems();

  const launchEmbeddedSignup = () => {
    if (!window.FB) {
      setError('Facebook SDK is not loaded yet. Please try again in a moment.');
      return;
    }
    if (setupProblems.length > 0) {
      setError(setupProblems[0]);
      return;
    }

    setError(null);
    setMessage('Meta\'s signup window is opening. If nothing appears, allow pop-ups for this site and click the button again.');
    sessionInfoRef.current = { wabaId: null, phoneNumberId: null, businessId: null, issue: null };

    window.FB.login(
      (response) => {
        setMessage(null);
        if (response.authResponse && response.authResponse.code) {
          setSignupResult({
            code: response.authResponse.code,
            wabaId: sessionInfoRef.current.wabaId,
            phoneNumberId: sessionInfoRef.current.phoneNumberId,
            businessId: sessionInfoRef.current.businessId,
          });
          setMessage('Signup completed. Review the details below and submit to provision the sender.');
        } else {
          setError(sessionInfoRef.current.issue || 'WA Embedded Signup was cancelled or did not complete.');
        }
      },
      {
        config_id: process.env.REACT_APP_WA_META_EMBEDDED_SIGNUP_CONFIG_ID,
        response_type: 'code',
        override_default_response_type: true,
        extras: EMBEDDED_SIGNUP_V4_EXTRAS,
      }
    );
  };

  const openHostedSignup = () => {
    if (hostedProblems.length > 0) {
      setError(hostedProblems[0]);
      return;
    }
    setError(null);
    setSignupResult(null);
    window.open(hostedSignupUrl(), 'wa-meta-hosted-embedded-signup', hostedSignupWindowFeatures());
    setMessage('Meta\'s hosted signup window is opening. Finish it there - Meta reports the completed signup to the service by webhook, '
      + 'so there is nothing to submit on this page. If nothing appears, allow pop-ups for this site and click the button again.');
  };

  const handleSubmit = async () => {
    if (!signupResult) {
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const response = await waApi.post(
        `${process.env.REACT_APP_SET_WA_EMBEDDED_SIGNUP_ENDPOINT}`,
        signupResult
      );
      const { message: respMessage, httpStatus } = response.data;
      if (httpStatus === 200) {
        setMessage('WABA and sender provisioning completed successfully.');
        setSignupResult(null);
      } else {
        setError(`Error: ${respMessage}`);
      }
    } catch (err) {
      setError(err.message || 'An error occurred while completing the signup.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="page-container">
      <h1>WA Application Embedded Signup</h1>
      <p>
        Use WA's Embedded Signup flow to let a client connect their own WA Business
        Account and phone number. On completion, submit the result below to provision the
        matching WA Business Account and Sender records. Or let Meta host the signup page instead.
      </p>

      {(setupProblems.length > 0 || sdkError) && (
        <div role="alert" style={{ background: "#fffbeb", border: "1px solid #fde68a", color: "#92400e", padding: "12px", borderRadius: "6px", marginBottom: "16px" }}>
          {[...setupProblems, ...(sdkError ? [sdkError] : [])].map((problem) => (
            <div key={problem}>{problem}</div>
          ))}
        </div>
      )}

      {error && (
        <div style={{ background: "#fef2f2", border: "1px solid #fecaca", color: "#b91c1c", padding: "12px", borderRadius: "6px", marginBottom: "16px" }}>
          {error}
        </div>
      )}

      {message && (
        <div style={{ background: "#f0fdf4", border: "1px solid #bbf7d0", color: "#15803d", padding: "12px", borderRadius: "6px", marginBottom: "16px" }}>
          {message}
        </div>
      )}

      <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '16px' }}>
        <button onClick={launchEmbeddedSignup} disabled={!sdkReady || setupProblems.length > 0}>
          {sdkError ? "WA Application SDK could not be loaded" : sdkReady ? 'Launch WA Embedded Signup' : 'Loading WA Application SDK...'}
        </button>

        <span style={OR_DIVIDER_STYLE}>
          <span aria-hidden="true" style={{ ...OR_LINE_STYLE, background: 'linear-gradient(90deg, transparent, #c9a4dc)' }} />
          <span style={OR_BADGE_STYLE}>OR</span>
          <span aria-hidden="true" style={{ ...OR_LINE_STYLE, background: 'linear-gradient(90deg, #c9a4dc, transparent)' }} />
        </span>

        <button onClick={openHostedSignup} disabled={hostedProblems.length > 0}>
          Meta-hosted Embedded Signup
          <span aria-hidden="true" style={{ marginLeft: '8px' }}>&#8599;</span>
        </button>
      </div>

      {signupResult && (
        <div className="page-container" style={{ marginTop: '20px' }}>
          <h3>Signup Result</h3>
          <div className="table-responsive">
          <table className="notifications-table">
            <tbody>
              <tr>
                <th>Authorization Code</th>
                <td>{signupResult.code}</td>
              </tr>
              <tr>
                <th>Business Account Id</th>
                <td>{signupResult.wabaId ?? 'Not returned'}</td>
              </tr>
              <tr>
                <th>Business Id</th>
                <td>{signupResult.businessId ?? 'Not returned'}</td>
              </tr>
              <tr>
                <th>Phone Number Id</th>
                <td>{signupResult.phoneNumberId ?? 'Not returned'}</td>
              </tr>
            </tbody>
          </table>
          </div>
          <button onClick={handleSubmit} disabled={submitting} style={{ marginTop: '12px' }}>
            {submitting ? 'Submitting...' : 'Submit and Provision'}
          </button>
        </div>
      )}
    </div>
  );
};

/**
 * Embedded Signup is for a SYSTEM_ADMIN or an ADMIN (the canManageWhatsAppSetup flag, as on the WABA and Sender screens). A USER has no side
 * panel entry or dashboard tile for it, and by its address only gets this notice - Meta's SDK is not even loaded for them. The flow itself
 * is a separate component so that none of its effects run until access is known to be there.
 */
const WaEmbededSignupPage = () => {
  const { canManageWhatsAppSetup, loaded } = useUserProfileAccess();

  if (!loaded) {
    return <div className="page-container">Loading...</div>;
  }

  if (!canManageWhatsAppSetup) {
    return (
      <div className="page-container">
        <h1>WA Application Embedded Signup</h1>
        <div className="profile-empty">Only a SYSTEM_ADMIN or ADMIN can use Embedded Signup.</div>
      </div>
    );
  }

  return <EmbeddedSignupFlow />;
};

export default WaEmbededSignupPage;
